import React, { useState, useEffect } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// PDF export now uses backend API with pdfkit for Lao language support
import { useTranslation } from '../hooks/useTranslation';

// Timezone helper - dates from backend are already in GMT+7 (Asia/Bangkok)
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleString('en-GB', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};
const formatDate = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Helper: Convert YYYY-MM-DD to DD/MM/YYYY for display
const toDisplayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

// Helper: Convert DD/MM/YYYY to YYYY-MM-DD for backend
const toISODate = (displayDate) => {
  if (!displayDate) return '';
  const parts = displayDate.split('/');
  if (parts.length !== 3) return displayDate;
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export default function Reports() {
  const { t, translate } = useTranslation();
  const [reportData, setReportData] = useState({
    transactions: [],
    topupTransactions: [],
    barrierTransactions: [],
    wimTransactions: [],
    vehicles: [],
    cards: [],
    users: [],
    revenue: [],
    stats: null
  });
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState('transactions');
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0], // Today
    end: new Date().toISOString().split('T')[0]    // Today
  });

  const reportTypes = [
    { value: 'transactions', label: translate('reports.transaction_report'), icon: '📊' },
    { value: 'topup', label: 'Topup Transactions', icon: '💵' },
    { value: 'barrier', label: 'Barrier Transactions', icon: '🚧' },
    { value: 'wim', label: 'WIM Fine Payments', icon: '⚖️' },
    { value: 'revenue', label: translate('reports.revenue_report'), icon: '💰' },
    { value: 'vehicles', label: translate('reports.vehicle_report'), icon: '🚗' },
    { value: 'cards', label: translate('reports.card_report'), icon: '💳' },
    { value: 'users', label: translate('reports.user_report'), icon: '👥' },
    { value: 'summary', label: translate('reports.summary_report'), icon: '📈' }
  ];

  const fetchRealData = React.useCallback(async () => {
    try {
      setLoading(true);

      console.log('📊 Fetching transactions with date range:', dateRange);

      // Fetch real transactions from database
      const transactionsResponse = await API.get('/reports/all-transactions', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          limit: 5000
        }
      }).catch(err => {
        console.error('Failed to fetch transactions:', err);
        console.error('📊 Backend error response:', err.response?.data);
        if (err.response?.data) {
          toast.error(`Backend Error: ${err.response.data.error || err.response.data.message}`);
        }
        return { data: { combined: [], topup: [], barrier: [], wim: [], stats: null } };
      });

      console.log('✅ API Response:', transactionsResponse.data);
      console.log('📦 Combined transactions:', transactionsResponse.data.combined?.length || 0);
      console.log('💵 Topup transactions:', transactionsResponse.data.topup?.length || 0);
      console.log('🚧 Barrier transactions:', transactionsResponse.data.barrier?.length || 0);

      // Fetch vehicles, cards, users from respective endpoints (with error handling)
      const [vehiclesRes, cardsRes, usersRes] = await Promise.all([
        API.get('/vehicles').catch(err => {
          console.warn('Failed to fetch vehicles:', err.message);
          return { data: [] };
        }),
        API.get('/admin/mifare-cards').catch(err => {
          console.warn('Failed to fetch cards:', err.message);
          return { data: [] };
        }),
        API.get('/admin/users').catch(err => {
          console.warn('Failed to fetch users:', err.message);
          return { data: [] };
        })
      ]);

      const newData = {
        transactions: transactionsResponse.data.combined || [],
        topupTransactions: transactionsResponse.data.topup || [],
        barrierTransactions: transactionsResponse.data.barrier || [],
        wimTransactions: transactionsResponse.data.wim || [],
        vehicles: vehiclesRes.data || [],
        cards: cardsRes.data || [],
        users: usersRes.data || [],
        revenue: [],
        stats: transactionsResponse.data.stats
      };

      console.log('💾 Setting report data:', {
        transactions: newData.transactions.length,
        topup: newData.topupTransactions.length,
        barrier: newData.barrierTransactions.length,
        wim: newData.wimTransactions.length
      });

      setReportData(newData);
      toast.success('Report data loaded successfully');

    } catch (error) {
      console.error('❌ Error fetching real data:', error);
      console.error('📋 Error response:', error.response?.data);
      console.error('📋 Error message:', error.message);

      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      const errorDetail = error.response?.data?.detail;

      toast.error(`Failed to load report data: ${errorMsg}${errorDetail ? ' - ' + errorDetail : ''}`, {
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Auto-fetch data when dateRange changes
  useEffect(() => {
    fetchRealData();
  }, [fetchRealData]);

  const generateReport = async () => {
    setLoading(true);
    try {
      // Fetch real transactions from API based on date range
      const response = await API.get('/reports/all-transactions', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          limit: 5000
        }
      });

      // Update report data with real transactions
      setReportData(prev => ({
        ...prev,
        transactions: response.data.combined || [],
        topupTransactions: response.data.topup || [],
        barrierTransactions: response.data.barrier || [],
        wimTransactions: response.data.wim || [],
        stats: response.data.stats
      }));

      toast.success(translate('reports.success_messages.report_generated'));
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(translate('reports.error_messages.generation_failed'));
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (data, filename) => {
    try {
      console.log('📊 Exporting report:', selectedReport);
      console.log('📦 Data type:', Array.isArray(data) ? 'Array' : typeof data);
      console.log('📦 Data length:', Array.isArray(data) ? data.length : 'N/A');
      console.log('📌 Date range:', dateRange);

      // Handle summary report - it returns an object, not an array
      if (selectedReport === 'summary' && typeof data === 'object' && !Array.isArray(data)) {
        // Export the revenue data from summary report
        if (data.revenue && Array.isArray(data.revenue)) {
          const revenueData = data.revenue.map(r => ({
            ...r,
            date: formatDate(r.date)
          }));
          const totals = {
            date: 'TOTAL',
            total_revenue: revenueData.reduce((sum, r) => sum + (parseFloat(r.total_revenue) || 0), 0),
            transactions_count: revenueData.reduce((sum, r) => sum + (parseInt(r.transactions_count) || 0), 0),
            vehicles_passed: revenueData.reduce((sum, r) => sum + (parseInt(r.vehicles_passed) || 0), 0),
            avg_transaction: revenueData.length > 0 ?
              revenueData.reduce((sum, r) => sum + (parseFloat(r.total_revenue) || 0), 0) / Math.max(revenueData.reduce((sum, r) => sum + (parseInt(r.transactions_count) || 0), 0), 1) : 0,
            mifare_revenue: revenueData.reduce((sum, r) => sum + (parseFloat(r.mifare_revenue) || 0), 0),
            obu_revenue: revenueData.reduce((sum, r) => sum + (parseFloat(r.obu_revenue) || 0), 0)
          };
          revenueData.push(totals);

          const worksheet = XLSX.utils.json_to_sheet(revenueData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Revenue Summary');

          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
          toast.success(translate('reports.success_messages.excel_exported'));
          return;
        } else {
          toast.error('ບໍ່ມີຂໍ້ມູນລາຍງານສະຫຼຸບເພື່ອສົ່ງອອກ (No summary data to export)');
          return;
        }
      }

      // Ensure data is an array for other report types
      if (!Array.isArray(data)) {
        console.error('Data is not an array:', data);
        toast.error('ຮູບແບບຂໍ້ມູນບໍ່ຖືກຕ້ອງ (Invalid data format)');
        return;
      }

      // Prepare export data with metadata header
      const reportTitle = reportTypes.find(r => r.value === selectedReport)?.label || 'Report';
      const metadata = [
        { '': 'SMART TOLL SYSTEM - Transaction Report' },
        { '': `Report Type: ${reportTitle}` },
        { '': `Period: ${dateRange.start} to ${dateRange.end}` },
        { '': `Generated: ${new Date().toLocaleString('en-GB')}` },
        { '': `Total Records: ${data.length}` },
        { '': '' } // Empty row
      ];

      // Add total summary row based on report type
      let exportData = [...data];

      if (selectedReport === 'transactions' || selectedReport === 'topup' || selectedReport === 'barrier' || selectedReport === 'wim') {
        // Format dates to DD/MM/YYYY HH:MM:SS in GMT+7 before export
        // Use "No." column with sequential numbers (1, 2, 3...) instead of database id
        exportData = data.map((t, index) => {
          // Create new object with No. as first column, excluding 'id'

          return {
            'No.': index + 1,
            transaction_id: t.transaction_id || t.id,
            date_time: formatDateTime(t.date_time),
            date: formatDate(t.date),
            transaction_type: t.transaction_type,
            license_plate: t.license_plate,
            method: t.method,
            amount: t.amount,
            status: t.status,
            description: t.description,
            user_email: t.user_email || '',
            gate_id: t.gate_id
          };
        });

        const totalAmount = data.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
        exportData.push({
          'No.': '----',
          transaction_id: '',
          date_time: 'TOTAL',
          date: '',
          transaction_type: `${data.length} records`,
          license_plate: '',
          method: '',
          amount: totalAmount,
          status: '',
          description: `Total: ${totalAmount.toLocaleString()} LAK`,
          user_email: '',
          gate_id: ''
        });
      } else if (selectedReport === 'revenue') {
        // Safety check: ensure data array exists and has valid revenue objects
        if (!Array.isArray(data) || data.length === 0) {
          console.warn('Revenue data is empty or invalid');
          toast.error('ບໍ່ມີຂໍ້ມູນລາຍງານເພື່ອສົ່ງອອກ (No revenue data to export)');
          return;
        }

        // Filter revenue data by selected date range to ensure export matches the filter
        const filteredData = data.filter(r => {
          if (!r.date) return false;
          // Normalize the date to YYYY-MM-DD for comparison
          let dateStr = r.date;
          if (dateStr instanceof Date) {
            dateStr = dateStr.toISOString().split('T')[0];
          } else if (typeof dateStr === 'string' && dateStr.includes('T')) {
            dateStr = dateStr.split('T')[0];
          }
          return dateStr >= dateRange.start && dateStr <= dateRange.end;
        });

        console.log('📊 Revenue export - Total data:', data.length, 'Filtered by date range:', filteredData.length);
        console.log('📅 Filter range:', dateRange.start, 'to', dateRange.end);

        if (filteredData.length === 0) {
          toast.error('ບໍ່ມີຂໍ້ມູນລາຍຮັບໃນຊ່ວງວັນທີທີ່ເລືອກ (No revenue data in selected date range)');
          return;
        }

        // Format dates to DD/MM/YYYY
        exportData = filteredData.map(r => ({
          ...r,
          date: formatDate(r.date)
        }));

        const totals = {
          date: 'TOTAL',
          total_revenue: filteredData.reduce((sum, r) => sum + (parseFloat(r.total_revenue) || 0), 0),
          transactions_count: filteredData.reduce((sum, r) => sum + (parseInt(r.transactions_count) || 0), 0),
          vehicles_passed: filteredData.reduce((sum, r) => sum + (parseInt(r.vehicles_passed) || 0), 0),
          avg_transaction: filteredData.length > 0 ?
            filteredData.reduce((sum, r) => sum + (parseFloat(r.total_revenue) || 0), 0) / Math.max(filteredData.reduce((sum, r) => sum + (parseInt(r.transactions_count) || 0), 0), 1) : 0,
          mifare_revenue: filteredData.reduce((sum, r) => sum + (parseFloat(r.mifare_revenue) || 0), 0),
          obu_revenue: filteredData.reduce((sum, r) => sum + (parseFloat(r.obu_revenue) || 0), 0)
        };
        exportData.push(totals);
      } else if (selectedReport === 'cards') {
        const totalBalance = data.reduce((sum, c) => sum + parseFloat(c.balance), 0);
        exportData.push({
          card_uid: 'TOTAL',
          card_type: `${data.length} cards`,
          balance: totalBalance,
          status: `Active: ${data.filter(c => c.status === 'active').length}`,
          create_date: '',
          expire_date: '',
          notes: `Total Balance: ${totalBalance.toLocaleString()} LAK`
        });
      } else if (selectedReport === 'vehicles') {
        exportData.push({
          license_plate: 'TOTAL',
          vehicle_type: `${data.length} vehicles`,
          vehicle_model: '',
          owner_name: `Mifare: ${data.filter(v => v.device_type === 'mifare').length}`,
          owner_phone: `OBU: ${data.filter(v => v.device_type === 'obu' || v.device_type === 'sbu').length}`,
          device_type: '',
          device_id: '',
          created_at: ''
        });
      } else if (selectedReport === 'users') {
        exportData.push({
          name: 'TOTAL',
          email: `${data.length} users`,
          phone: '',
          role: `Admin: ${data.filter(u => u.role === 'admin').length} | User: ${data.filter(u => u.role === 'user').length}`,
          status: `Active: ${data.filter(u => u.status === 'active').length}`,
          verified: `Verified: ${data.filter(u => u.verified).length}`,
          created_at: ''
        });
      }

      // Update Total Records in metadata to reflect actual exported count (excluding totals row)
      const actualRecordCount = exportData.length > 0 ? exportData.length - 1 : 0;
      metadata[4] = { '': `Total Records: ${actualRecordCount}` };

      // Create worksheet with metadata at top
      const worksheet = XLSX.utils.json_to_sheet(metadata, { skipHeader: true });
      XLSX.utils.sheet_add_json(worksheet, exportData, { origin: -1, skipHeader: false });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

      // Set column widths
      const maxWidth = 20;
      const colWidths = [];
      if (exportData.length > 0) {
        Object.keys(exportData[0]).forEach(() => {
          colWidths.push({ wch: maxWidth });
        });
        worksheet['!cols'] = colWidths;
      }

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(translate('reports.success_messages.excel_exported'));
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(translate('reports.error_messages.excel_export_failed'));
    }
  };

  // Export to PDF using backend API with Lao language support (pdfkit)
  const exportToPDF = async (data, title) => {
    try {
      // Map selectedReport to backend report_type
      let reportType = selectedReport;
      if (selectedReport === 'transactions') {
        reportType = 'transactions';
      } else if (selectedReport === 'topup') {
        reportType = 'topup';
      } else if (selectedReport === 'barrier') {
        reportType = 'barrier';
      } else if (selectedReport === 'wim') {
        reportType = 'wim';
      }

      toast.loading('ກຳລັງສ້າງ PDF... (Generating PDF...)', { id: 'pdf-generating' });

      // Call backend API to generate PDF with Lao font support
      // Use longer timeout for large PDF generation (120 seconds)
      const response = await API.get('/report-pdf/transactions', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          report_type: reportType
        },
        responseType: 'blob',
        timeout: 120000 // 120 seconds timeout for large PDF generation
      });

      toast.dismiss('pdf-generating');

      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Open PDF in new tab for preview
      window.open(blobUrl, '_blank');

      // Also trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${title.replace(/\s+/g, '_')}_${dateRange.start}_to_${dateRange.end}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);

      toast.success(translate('reports.success_messages.pdf_exported') + ' (ພາສາລາວ)');
    } catch (error) {
      toast.dismiss('pdf-generating');
      console.error('PDF export error:', error);

      // If backend fails, show detailed error
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`${translate('reports.error_messages.pdf_export_failed')}: ${errorMessage}`);
    }
  };

  const getCurrentReportData = () => {
    console.log('🔍 getCurrentReportData for:', selectedReport);
    console.log('Date range:', dateRange);
    console.log('Report data available:', {
      transactions: reportData.transactions.length,
      topup: reportData.topupTransactions.length,
      barrier: reportData.barrierTransactions.length,
      wim: reportData.wimTransactions?.length || 0
    });

    switch (selectedReport) {
      case 'transactions':
        // Backend already filters by date range, no need to filter again
        const allTrans = reportData.transactions;
        console.log('📊 All transactions:', allTrans.length);
        if (allTrans.length > 0) {
          console.log('Sample transaction:', allTrans[0]);
        }
        return allTrans;
      case 'topup':
        // Backend already filters by date range
        const topupTrans = reportData.topupTransactions || [];
        console.log('💵 Topup transactions:', topupTrans.length);
        return topupTrans;
      case 'barrier':
        // Backend already filters by date range
        const barrierTrans = reportData.barrierTransactions || [];
        console.log('🚧 Barrier transactions:', barrierTrans.length);
        return barrierTrans;
      case 'wim':
        // Backend already filters by date range
        const wimTrans = reportData.wimTransactions || [];
        console.log('⚖️ WIM transactions:', wimTrans.length);
        return wimTrans;
      case 'revenue':
        // Calculate revenue from barrier transactions only (toll payment transactions)
        // This matches the database query from operator which only includes barrier_transactions
        const barrierTransForRevenue = reportData.barrierTransactions || [];

        console.log('📊 Revenue calculation - Total barrier transactions:', barrierTransForRevenue.length);

        // Group by date
        const revenueByDate = {};
        let nullDateCount = 0;

        barrierTransForRevenue.forEach(t => {
          // Ensure date is a string in YYYY-MM-DD format
          let date = t.date;

          // Handle null/undefined dates
          if (!date) {
            nullDateCount++;
            // Extract date from date_time if date is null
            if (t.date_time) {
              const dt = new Date(t.date_time);
              date = dt.toISOString().split('T')[0];
            } else {
              return; // Skip transactions with no date
            }
          }

          // Normalize date format (in case it comes as Date object)
          if (date instanceof Date) {
            date = date.toISOString().split('T')[0];
          } else if (typeof date === 'string' && date.includes('T')) {
            date = date.split('T')[0];
          }

          if (!revenueByDate[date]) {
            revenueByDate[date] = {
              date: date,
              total_revenue: 0,
              transactions_count: 0,
              vehicles_passed: 0,
              mifare_revenue: 0,
              obu_revenue: 0
            };
          }
          revenueByDate[date].total_revenue += parseFloat(t.amount) || 0;
          revenueByDate[date].transactions_count++;
          revenueByDate[date].vehicles_passed++;

          if (t.method?.toLowerCase().includes('mifare')) {
            revenueByDate[date].mifare_revenue += parseFloat(t.amount) || 0;
          } else {
            revenueByDate[date].obu_revenue += parseFloat(t.amount) || 0;
          }
        });

        if (nullDateCount > 0) {
          console.warn('⚠️ Found', nullDateCount, 'transactions with null date');
        }

        const revenueResult = Object.values(revenueByDate).map(r => ({
          ...r,
          avg_transaction: r.transactions_count > 0 ? r.total_revenue / r.transactions_count : 0
        }))
          // Filter by selected date range to ensure accuracy
          .filter(r => {
            if (!r.date) return false;
            return r.date >= dateRange.start && r.date <= dateRange.end;
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        console.log('📊 Revenue result by date:', revenueResult.map(r => ({ date: r.date, count: r.transactions_count })));
        console.log('📅 Filtered by date range:', dateRange.start, 'to', dateRange.end);

        // Convert to array and calculate average
        return revenueResult;
      case 'vehicles':
        return reportData.vehicles;
      case 'cards':
        return reportData.cards;
      case 'users':
        return reportData.users;
      case 'summary':
        // Backend already filters by date range
        const summaryTransactions = reportData.transactions;

        // Calculate revenue from transactions
        const summaryRevenueByDate = {};
        summaryTransactions.forEach(t => {
          const date = t.date;
          if (!summaryRevenueByDate[date]) {
            summaryRevenueByDate[date] = {
              date: date,
              total_revenue: 0,
              transactions_count: 0,
              vehicles_passed: 0,
              mifare_revenue: 0,
              obu_revenue: 0
            };
          }
          summaryRevenueByDate[date].total_revenue += parseFloat(t.amount);
          summaryRevenueByDate[date].transactions_count++;
          summaryRevenueByDate[date].vehicles_passed++;

          if (t.method?.toLowerCase().includes('mifare')) {
            summaryRevenueByDate[date].mifare_revenue += parseFloat(t.amount);
          } else {
            summaryRevenueByDate[date].obu_revenue += parseFloat(t.amount);
          }
        });

        const calculatedRevenue = Object.values(summaryRevenueByDate).map(r => ({
          ...r,
          avg_transaction: r.transactions_count > 0 ? r.total_revenue / r.transactions_count : 0
        })).sort((a, b) => new Date(b.date) - new Date(a.date));

        return {
          transactions: summaryTransactions,
          revenue: calculatedRevenue,
          vehicles: reportData.vehicles,
          cards: reportData.cards,
          users: reportData.users,
          stats: reportData.stats
        };
      default:
        return [];
    }
  };

  const handleReprint = async (transaction) => {
    try {
      const type = transaction.transaction_type;
      const id = transaction.id;
      const transId = transaction.transaction_id || id;
      let url = '';
      if (type === 'topup') {
        url = `/admin/topup-receipt-pdf/${transId}?reprint=true`;
      } else if (type === 'barrier') {
        url = `/manual-payment/receipt-pdf/${transId}?reprint=true`;
      } else if (type === 'wim') {
        url = `/wim/receipt-pdf/${transId}?reprint=true`;
      } else {
        toast.error('Unknown transaction type');
        return;
      }

      toast.loading('Generating PDF...', { id: 'pdf-reprint' });
      const response = await API.get(url, { responseType: 'blob' });
      toast.dismiss('pdf-reprint');

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      toast.dismiss('pdf-reprint');
      toast.error('Failed to reprint receipt');
      console.error('Reprint error:', error);
    }
  };

  const handleCancel = async (transaction) => {
    // Only allow cancelling successful transactions
    if (transaction.status === 'cancelled') {
      toast.error('This transaction is already cancelled');
      return;
    }

    const confirmMsg = `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການຍົກເລີກທຸລະກຳນີ້?\n(Are you sure you want to cancel this ${transaction.transaction_type} transaction?)`;
    const reason = window.prompt(`${confirmMsg}\n\nກະລຸນາໃສ່ເຫດຜົນ (Reason):`);

    if (reason === null) return; // User clicked Cancel in prompt

    try {
      toast.loading('ກຳລັງຍົກເລີກ... (Cancelling...)', { id: 'cancel-tx' });

      const response = await API.post('/admin/transactions/cancel', {
        id: transaction.id,
        type: transaction.transaction_type,
        reason: reason || 'No reason provided'
      });

      toast.dismiss('cancel-tx');

      if (response.data.success) {
        toast.success(translate('reports.success_messages.cancelled') || 'ຍົກເລີກທຸລະກຳສຳເລັດ');
        // Refresh data
        fetchRealData();
      } else {
        toast.error(response.data.message || 'Failed to cancel transaction');
      }
    } catch (error) {
      toast.dismiss('cancel-tx');
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to cancel: ${errorMsg}`);
      console.error('Cancel Error:', error);
    }
  };

  const renderReportTable = () => {
    const data = getCurrentReportData();

    if (selectedReport === 'summary') {
      return (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                {data.transactions.length}
              </div>
              <div className="text-blue-600 dark:text-blue-400 text-sm">{translate('reports.table_headers.total_transactions')}</div>
            </div>
            <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                {data.transactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0).toLocaleString()}
              </div>
              <div className="text-green-600 dark:text-green-400 text-sm">{translate('reports.table_headers.total_revenue_summary')}</div>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                {data.vehicles.length}
              </div>
              <div className="text-purple-600 dark:text-purple-400 text-sm">{translate('reports.table_headers.total_vehicles')}</div>
            </div>
            <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
                {data.cards.filter(c => c.status === 'active').length}
              </div>
              <div className="text-yellow-600 dark:text-yellow-400 text-sm">{translate('reports.table_headers.active_cards')}</div>
            </div>
          </div>

          {/* Daily Revenue Chart Data */}
          <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              📊 {translate('reports.table_headers.daily_revenue_summary')}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-600">
                    <th className="border p-3 text-left">{translate('reports.table_headers.date')}</th>
                    <th className="border p-3 text-right">{translate('dashboard.revenue_lak')}</th>
                    <th className="border p-3 text-right">{translate('reports.table_headers.transactions')}</th>
                    <th className="border p-3 text-right">{translate('reports.table_headers.vehicles')}</th>
                    <th className="border p-3 text-right">{translate('reports.table_headers.avg_transaction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.revenue.slice(0, 10).map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                      <td className="border p-3">{formatDate(item.date)}</td>
                      <td className="border p-3 text-right font-medium text-green-600">
                        {item.total_revenue.toLocaleString()}
                      </td>
                      <td className="border p-3 text-right">{item.transactions_count}</td>
                      <td className="border p-3 text-right">{item.vehicles_passed}</td>
                      <td className="border p-3 text-right">{item.avg_transaction.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                    <td className="border p-3 text-right text-lg">
                      📊 TOTAL
                    </td>
                    <td className="border p-3 text-right text-lg text-green-700 dark:text-green-300">
                      {data.revenue.slice(0, 10).reduce((sum, r) => sum + r.total_revenue, 0).toLocaleString()} LAK
                    </td>
                    <td className="border p-3 text-right text-lg">
                      {data.revenue.slice(0, 10).reduce((sum, r) => sum + r.transactions_count, 0).toLocaleString()}
                    </td>
                    <td className="border p-3 text-right text-lg">
                      {data.revenue.slice(0, 10).reduce((sum, r) => sum + r.vehicles_passed, 0).toLocaleString()}
                    </td>
                    <td className="border p-3 text-right text-lg">
                      {data.revenue.slice(0, 10).length > 0 ?
                        (data.revenue.slice(0, 10).reduce((sum, r) => sum + r.total_revenue, 0) /
                          data.revenue.slice(0, 10).reduce((sum, r) => sum + r.transactions_count, 0)).toLocaleString()
                        : 0} LAK
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'transactions' || selectedReport === 'topup' || selectedReport === 'barrier' || selectedReport === 'wim') {
      const transactionTypeLabel = selectedReport === 'topup' ? 'Topup' :
        selectedReport === 'barrier' ? 'Barrier' :
          selectedReport === 'wim' ? 'WIM Fine' : 'All';

      return (
        <div className="overflow-x-auto">
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>{transactionTypeLabel} Transactions:</strong> {data.length} records
              {reportData.stats && (
                <span className="ml-4">
                  | Total: <strong>{selectedReport === 'topup' ? reportData.stats.topup_total?.toLocaleString() :
                    selectedReport === 'barrier' ? reportData.stats.barrier_total?.toLocaleString() :
                      selectedReport === 'wim' ? reportData.stats.wim_total?.toLocaleString() :
                        ((reportData.stats.topup_total || 0) + (reportData.stats.barrier_total || 0) + (reportData.stats.wim_total || 0))?.toLocaleString()}</strong> LAK
                </span>
              )}
            </p>
          </div>

          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-600">
                <th className="border p-3 text-center" style={{ width: '50px' }}>No.</th>
                <th className="border p-3 text-left">Transaction ID</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.date_time')}</th>
                <th className="border p-3 text-left">Type</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.license_plate')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.method')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.amount_lak')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.status')}</th>
                <th className="border p-3 text-left">Description</th>
                <th className="border p-3 text-left">User Email</th>
                <th className="border p-3 text-center">Gate/Location</th>
                <th className="border p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((transaction, index) => (
                <tr key={`${transaction.transaction_type}-${transaction.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="border p-3 text-center font-medium">{index + 1}</td>
                  <td className="border p-3 font-mono text-xs">{transaction.transaction_id || transaction.id}</td>
                  <td className="border p-3 text-sm">{formatDateTime(transaction.date_time)}</td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${transaction.transaction_type?.toLowerCase() === 'topup' ? 'bg-green-100 text-green-800' :
                      (transaction.transaction_type?.toLowerCase() === 'barrier' || transaction.transaction_type?.toLowerCase() === 'spending') ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {transaction.transaction_type?.toLowerCase() === 'topup' ? '💵 Topup' :
                        (transaction.transaction_type?.toLowerCase() === 'barrier' || transaction.transaction_type?.toLowerCase() === 'spending') ? '🚧 barrier' :
                          '⚖️ WIM Fine'}
                    </span>
                  </td>
                  <td className="border p-3 font-bold text-sm">{transaction.license_plate || '-'}</td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${transaction.method?.toUpperCase() === 'CASH' ? 'bg-green-100 text-green-800' :
                      transaction.method?.toLowerCase().includes('mifare') ? 'bg-blue-100 text-blue-800' :
                        transaction.method?.toUpperCase() === 'OBU' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {transaction.method?.toUpperCase() === 'CASH' ? '💵 Cash' :
                        transaction.method?.toLowerCase().includes('mifare') ? '💳 Mifare' :
                          transaction.method?.toUpperCase() === 'OBU' ? '📡 OBU' :
                            transaction.method || '-'}
                    </span>
                  </td>
                  <td className="border p-3 text-right font-medium">{parseFloat(transaction.amount).toLocaleString()}</td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${transaction.status === 'success' ? 'bg-green-100 text-green-800' :
                      transaction.status === 'cancelled' ? 'bg-gray-100 text-gray-500 line-through' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {transaction.status === 'success' ? `✅ Success` :
                        transaction.status === 'cancelled' ? `⚪ Cancelled` :
                          `❌ Failed`}
                    </span>
                  </td>
                  <td className="border p-3 text-sm text-gray-600 dark:text-gray-400">
                    {transaction.description || '-'}
                  </td>
                  <td className="border p-3 text-sm text-gray-600 dark:text-gray-400">
                    {transaction.user_email || '-'}
                  </td>
                  <td className="border p-3 text-center text-sm">{transaction.gate_id || transaction.location || '-'}</td>
                  <td className="border p-3 text-center">
                    <div className="flex justify-center space-x-1">
                      <button
                        onClick={() => handleReprint(transaction)}
                        className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none transition-colors"
                        title="Re-print Receipt"
                      >
                        🖨️
                      </button>
                      {transaction.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancel(transaction)}
                          className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none transition-colors"
                          title="Cancel Transaction"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                <td colSpan="6" className="border p-3 text-right text-lg">
                  📊 TOTAL ({data.length} records)
                </td>
                <td className="border p-3 text-right text-lg text-green-700 dark:text-green-300">
                  {data.reduce((sum, t) => sum + parseFloat(t.amount), 0).toLocaleString()} LAK
                </td>
                <td colSpan="5" className="border p-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (selectedReport === 'vehicles') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-600">
                <th className="border p-3 text-left">{translate('reports.table_headers.license_plate')}</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.vehicle_type')}</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.model')}</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.owner')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.device_type')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.device_id')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.registered')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((vehicle, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="border p-3 font-bold">{vehicle.license_plate}</td>
                  <td className="border p-3">{vehicle.vehicle_type}</td>
                  <td className="border p-3">{vehicle.vehicle_model}</td>
                  <td className="border p-3">
                    <div>
                      <div className="font-medium">{vehicle.owner_name}</div>
                      <div className="text-sm text-gray-500">{vehicle.owner_phone}</div>
                    </div>
                  </td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${vehicle.device_type === 'mifare' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                      {vehicle.device_type === 'mifare' ? `💳 ${translate('reports.status_labels.mifare')}` : `📡 ${translate('reports.status_labels.obu_sbu')}`}
                    </span>
                  </td>
                  <td className="border p-3 text-center font-mono text-sm">{vehicle.device_id}</td>
                  <td className="border p-3 text-center">{formatDate(vehicle.created_at)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                <td colSpan="6" className="border p-3 text-right text-lg">
                  📊 TOTAL ({data.length} vehicles)
                </td>
                <td className="border p-3 text-sm text-gray-600 dark:text-gray-400">
                  Mifare: {data.filter(v => v.device_type === 'mifare').length} |
                  OBU: {data.filter(v => v.device_type === 'obu' || v.device_type === 'sbu').length}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (selectedReport === 'cards') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-600">
                <th className="border p-3 text-left">{translate('reports.table_headers.card_uid')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.type')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.balance_lak')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.status')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.created')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.expires')}</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((card, index) => {
                const isExpired = new Date(card.expire_date) < new Date();
                const expiringSoon = new Date(card.expire_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && !isExpired;

                return (
                  <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-600 ${isExpired ? 'bg-red-50 dark:bg-red-900/20' :
                    expiringSoon ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''
                    }`}>
                    <td className="border p-3 font-mono text-sm">{card.card_uid}</td>
                    <td className="border p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${card.card_type === 'premium' ? 'bg-purple-100 text-purple-800' :
                        card.card_type === 'student' ? 'bg-blue-100 text-blue-800' :
                          card.card_type === 'senior' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {card.card_type}
                      </span>
                    </td>
                    <td className={`border p-3 text-right font-medium ${card.balance === 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                      {card.balance.toLocaleString()}
                    </td>
                    <td className="border p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${card.status === 'active' ? 'bg-green-100 text-green-800' :
                        card.status === 'expired' ? 'bg-red-100 text-red-800' :
                          card.status === 'blocked' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {isExpired ? `⏰ ${translate('reports.status_labels.expired')}` :
                          expiringSoon ? `⚠️ ${translate('reports.status_labels.expiring')}` :
                            `✓ ${translate(`reports.status_labels.${card.status}`)}`}
                      </span>
                    </td>
                    <td className="border p-3 text-center">{formatDate(card.create_date)}</td>
                    <td className="border p-3 text-center">{formatDate(card.expire_date)}</td>
                    <td className="border p-3 text-sm text-gray-600">{card.notes || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                <td colSpan="2" className="border p-3 text-right text-lg">
                  📊 TOTAL ({data.length} cards)
                </td>
                <td className="border p-3 text-right text-lg text-green-700 dark:text-green-300">
                  {data.reduce((sum, c) => sum + parseFloat(c.balance), 0).toLocaleString()} LAK
                </td>
                <td colSpan="4" className="border p-3 text-sm text-gray-600 dark:text-gray-400">
                  Active: {data.filter(c => c.status === 'active').length} |
                  Expired: {data.filter(c => new Date(c.expire_date) < new Date()).length}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (selectedReport === 'users') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-600">
                <th className="border p-3 text-left">{translate('reports.table_headers.name')}</th>
                <th className="border p-3 text-left">{translate('reports.table_headers.email')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.phone')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.role')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.status')}</th>
                <th className="border p-3 text-center">{translate('users.verified')}</th>
                <th className="border p-3 text-center">{translate('reports.table_headers.created')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((user, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="border p-3 font-medium">{user.name}</td>
                  <td className="border p-3 text-sm">{user.email}</td>
                  <td className="border p-3 text-center font-mono text-sm">{user.phone}</td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                      {user.role === 'admin' ? `🔑 ${translate('reports.status_labels.admin')}` : `👤 ${translate('reports.status_labels.user')}`}
                    </span>
                  </td>
                  <td className="border p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' :
                      user.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                        user.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {user.status === 'active' ? `✅ ${translate('reports.status_labels.active')}` :
                        user.status === 'suspended' ? `⏸️ ${translate('reports.status_labels.suspended')}` :
                          user.status === 'cancelled' ? `❌ ${translate('reports.status_labels.cancelled')}` :
                            `⏳ ${translate('reports.status_labels.inactive')}`}
                    </span>
                  </td>
                  <td className="border p-3 text-center">
                    {user.verified ? (
                      <span className="text-green-600">✓ {translate('reports.status_labels.verified')}</span>
                    ) : (
                      <span className="text-orange-600">⏳ {translate('reports.status_labels.unverified')}</span>
                    )}
                  </td>
                  <td className="border p-3 text-center">{formatDate(user.created_at)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                <td colSpan="6" className="border p-3 text-right text-lg">
                  📊 TOTAL ({data.length} users)
                </td>
                <td className="border p-3 text-sm text-gray-600 dark:text-gray-400">
                  Admin: {data.filter(u => u.role === 'admin').length} |
                  User: {data.filter(u => u.role === 'user').length} |
                  Active: {data.filter(u => u.status === 'active').length} |
                  Verified: {data.filter(u => u.verified).length}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (selectedReport === 'revenue') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-600">
                <th className="border p-3 text-left">{translate('reports.table_headers.date')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.total_revenue_lak')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.transactions')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.vehicles')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.avg_transaction')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.mifare_revenue')}</th>
                <th className="border p-3 text-right">{translate('reports.table_headers.obu_revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((revenue, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="border p-3 font-medium">{formatDate(revenue.date)}</td>
                  <td className="border p-3 text-right font-bold text-green-600">
                    {revenue.total_revenue.toLocaleString()}
                  </td>
                  <td className="border p-3 text-right">{revenue.transactions_count}</td>
                  <td className="border p-3 text-right">{revenue.vehicles_passed}</td>
                  <td className="border p-3 text-right">{revenue.avg_transaction.toLocaleString()}</td>
                  <td className="border p-3 text-right text-blue-600">
                    {revenue.mifare_revenue.toLocaleString()}
                  </td>
                  <td className="border p-3 text-right text-purple-600">
                    {revenue.obu_revenue.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-100 dark:bg-blue-900 font-bold">
                <td className="border p-3 text-right text-lg">
                  📊 TOTAL ({data.length} days)
                </td>
                <td className="border p-3 text-right text-lg text-green-700 dark:text-green-300">
                  {data.reduce((sum, r) => sum + r.total_revenue, 0).toLocaleString()} LAK
                </td>
                <td className="border p-3 text-right text-lg">
                  {data.reduce((sum, r) => sum + r.transactions_count, 0).toLocaleString()}
                </td>
                <td className="border p-3 text-right text-lg">
                  {data.reduce((sum, r) => sum + r.vehicles_passed, 0).toLocaleString()}
                </td>
                <td className="border p-3 text-right text-lg">
                  {data.length > 0 ? (data.reduce((sum, r) => sum + r.total_revenue, 0) / data.reduce((sum, r) => sum + r.transactions_count, 0)).toLocaleString() : 0} LAK
                </td>
                <td className="border p-3 text-right text-lg text-blue-700 dark:text-blue-300">
                  {data.reduce((sum, r) => sum + r.mifare_revenue, 0).toLocaleString()} LAK
                </td>
                <td className="border p-3 text-right text-lg text-purple-700 dark:text-purple-300">
                  {data.reduce((sum, r) => sum + r.obu_revenue, 0).toLocaleString()} LAK
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    // Fallback for unknown report types
    return <div className="text-center text-gray-500 py-8">{translate('reports.no_data')}</div>;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          📈 {translate('reports.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {translate('reports.subtitle')}
        </p>
      </div>

      {/* Report Configuration */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {translate('reports.report_type')}
          </label>
          <select
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={selectedReport}
            onChange={(e) => setSelectedReport(e.target.value)}
          >
            {reportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {translate('reports.start_date')}
          </label>
          <div className="relative flex items-center">
            <span className="absolute left-2 text-gray-700 dark:text-white pointer-events-none z-10">
              {toDisplayDate(dateRange.start)}
            </span>
            <input
              type="date"
              className="w-full p-2 pl-28 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white cursor-pointer"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {translate('reports.end_date')}
          </label>
          <div className="relative flex items-center">
            <span className="absolute left-2 text-gray-700 dark:text-white pointer-events-none z-10">
              {toDisplayDate(dateRange.end)}
            </span>
            <input
              type="date"
              className="w-full p-2 pl-28 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white cursor-pointer"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-end">
          <button
            onClick={generateReport}
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg text-white font-medium transition-colors duration-200 ${loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600'
              }`}
          >
            {loading ? `🔄 ${translate('reports.generating')}` : `📊 ${translate('reports.create_report')}`}
          </button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => exportToExcel(getCurrentReportData(), selectedReport)}
          disabled={getCurrentReportData().length === 0}
          className={`py-2 px-4 rounded-lg text-white transition-colors duration-200 ${getCurrentReportData().length === 0
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-500 hover:bg-green-600'
            }`}
        >
          📊 {translate('reports.export_excel')}
        </button>
        <button
          onClick={() => exportToPDF(getCurrentReportData(), reportTypes.find(r => r.value === selectedReport)?.label || 'Report')}
          disabled={getCurrentReportData().length === 0}
          className={`py-2 px-4 rounded-lg text-white transition-colors duration-200 ${getCurrentReportData().length === 0
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-red-500 hover:bg-red-600'
            }`}
        >
          📄 {translate('reports.export_pdf')}
        </button>
      </div>

      {/* Report Content */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            {reportTypes.find(r => r.value === selectedReport)?.icon} {reportTypes.find(r => r.value === selectedReport)?.label}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {translate('reports.period')}: {dateRange.start} {translate('reports.to')} {dateRange.end}
          </span>
        </div>

        {renderReportTable()}
      </div>
    </div>
  );
}